import { useEffect, useMemo, useRef, useState } from "react";
import { fetchPresenceStatuses } from "../services/presenceService";
import { usePresenceOptional } from "../store/PresenceContext";
import { normalizeProfileUserId } from "../utils/profileRoutes";

const STATUS_UNKNOWN = "unknown";
const PRESENCE_WARMUP_POLL_INTERVAL = 2500;
const PRESENCE_WARMUP_RETRY_LIMIT = 2;

const mapEntryToState = (entry) => {
    if (!entry) {
        return { status: STATUS_UNKNOWN, lastSeen: null };
    }

    const rawStatus = entry.status ?? (typeof entry.online === "boolean" ? (entry.online ? "online" : "offline") : null);
    const normalizedStatus = rawStatus ? String(rawStatus).toLowerCase() : STATUS_UNKNOWN;
    const allowed = new Set(["online", "offline", "away"]);

    return {
        status: allowed.has(normalizedStatus) ? normalizedStatus : STATUS_UNKNOWN,
        lastSeen: entry.lastSeen ?? null,
    };
};

const shouldUseWarmupRetry = (mapped, attempt, retryLimit) => (
    attempt < retryLimit
    && mapped
    && mapped.status !== "online"
    && !mapped.lastSeen
);

const isSamePresenceEntry = (left, right) => (
    Boolean(left)
    && Boolean(right)
    && left.userId === right.userId
    && left.online === right.online
    && (left.lastSeen || null) === (right.lastSeen || null)
    && (left.status || undefined) === (right.status || undefined)
    && (left.epoch ?? null) === (right.epoch ?? null)
);

/**
 * useUserPresence
 * Provides the latest presence state for a single user id, optionally syncing with PresenceContext.
 *
 * @param {string|number} userId
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true]
 * @param {number} [options.pollInterval=45000]
 * @param {number} [options.warmupPollInterval=2500]
 * @param {number} [options.warmupRetryLimit=2]
 * @param {string} [options.initialStatus]
 * @param {string|null} [options.initialLastSeen]
 * @returns {{ status: 'online'|'offline'|'away'|'unknown', lastSeen: string|null, isLoading: boolean }}
 */
export default function useUserPresence(
    userId,
    {
        enabled = true,
        pollInterval = 45000,
        warmupPollInterval = PRESENCE_WARMUP_POLL_INTERVAL,
        warmupRetryLimit = PRESENCE_WARMUP_RETRY_LIMIT,
        initialStatus = STATUS_UNKNOWN,
        initialLastSeen = null,
    } = {},
) {
    const presenceContext = usePresenceOptional();
    const upsertStatus = presenceContext?.upsertStatus;
    const clearStatus = presenceContext?.clearStatus;
    const trackUsers = presenceContext?.trackUsers;
    const contextStatuses = presenceContext?.statuses;
    const [state, setState] = useState(() => ({
        status: initialStatus,
        lastSeen: initialLastSeen,
        isLoading: false,
    }));

    const normalizedUserId = useMemo(() => {
        return normalizeProfileUserId(userId) || undefined;
    }, [userId]);
    const currentContextEntry = normalizedUserId && contextStatuses
        ? contextStatuses[normalizedUserId]
        : undefined;
    const contextEntryRef = useRef(currentContextEntry);
    contextEntryRef.current = currentContextEntry;

    useEffect(() => {
        if (!enabled || !normalizedUserId) {
            setState({
                status: initialStatus ?? STATUS_UNKNOWN,
                lastSeen: initialLastSeen ?? null,
                isLoading: false,
            });
            return;
        }

        let cancelled = false;
        let timeoutId = null;
        const releaseTrackedUser = typeof trackUsers === "function"
            ? trackUsers([normalizedUserId])
            : null;

        setState({
            status: initialStatus ?? STATUS_UNKNOWN,
            lastSeen: initialLastSeen ?? null,
            isLoading: true,
        });

        function scheduleNextLoad(delayMs, nextAttempt) {
            if (!cancelled && pollInterval > 0 && typeof window !== "undefined") {
                timeoutId = window.setTimeout(() => load(nextAttempt), delayMs);
            }
        }

        async function load(attempt = 0) {
            let mapped = null;
            const contextEntryAtRequestStart = contextEntryRef.current;
            setState((prev) => ({ ...prev, isLoading: true }));
            try {
                const statuses = await fetchPresenceStatuses([normalizedUserId]);
                if (cancelled) {
                    return;
                }
                const entry = Array.isArray(statuses)
                    ? statuses.find((item) => normalizeProfileUserId(item?.userId) === normalizedUserId)
                    : null;
                mapped = mapEntryToState(entry);
                if (typeof upsertStatus === "function") {
                    if (entry) {
                        upsertStatus({
                            ...entry,
                            userId: normalizedUserId,
                        });
                        setState((prev) => ({ ...prev, isLoading: false }));
                    } else {
                        const latestContextEntry = contextEntryRef.current;
                        const contextUnchangedSinceRequest = contextEntryAtRequestStart
                            ? isSamePresenceEntry(latestContextEntry, contextEntryAtRequestStart)
                            : !latestContextEntry;

                        if (contextUnchangedSinceRequest) {
                            if (typeof clearStatus === "function") {
                                clearStatus(normalizedUserId, {
                                    expectedStatus: contextEntryAtRequestStart || null,
                                });
                            }
                            setState({ ...mapped, isLoading: false });
                        } else {
                            setState({
                                ...mapEntryToState(latestContextEntry),
                                isLoading: false,
                            });
                        }
                    }
                } else {
                    setState({ ...mapped, isLoading: false });
                }
            } catch (error) {
                if (!cancelled) {
                    setState((prev) => ({
                        status: prev.status ?? STATUS_UNKNOWN,
                        lastSeen: prev.lastSeen ?? null,
                        isLoading: false,
                    }));
                }
            } finally {
                const delayMs = shouldUseWarmupRetry(mapped, attempt, warmupRetryLimit)
                    ? warmupPollInterval
                    : pollInterval;
                scheduleNextLoad(delayMs, attempt + 1);
            }
        }

        load(0);

        return () => {
            cancelled = true;
            if (typeof releaseTrackedUser === "function") {
                releaseTrackedUser();
            }
            if (timeoutId && typeof window !== "undefined") {
                clearTimeout(timeoutId);
            }
        };
    }, [
        enabled,
        normalizedUserId,
        pollInterval,
        warmupPollInterval,
        warmupRetryLimit,
        upsertStatus,
        clearStatus,
        trackUsers,
        initialStatus,
        initialLastSeen,
    ]);

    useEffect(() => {
        if (!enabled || !normalizedUserId || !contextStatuses) {
            return;
        }
        const entry = contextStatuses[normalizedUserId];
        if (!entry) {
            return;
        }
        const mapped = mapEntryToState(entry);
        setState((prev) => ({
            status: mapped.status,
            lastSeen: mapped.lastSeen,
            isLoading: prev.isLoading,
        }));
    }, [enabled, normalizedUserId, contextStatuses]);

    return state;
}
